import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { pb } from '@/lib/pocketbase';
import type { AuthUser } from '@/lib/pocketbase';
import { setUserOnline, setUserOffline } from '@/utils/onlineStatusUtils';
import { logger } from '@/utils/secureLogger';

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, role: 'student' | 'tutor', phoneNumber?: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isLoggingOut: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export AuthContext for conditional context checking
export { AuthContext };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const refreshUser = async () => {
    try {
      if (pb.authStore.isValid && pb.authStore.model) {
        // Fetch fresh user data from the server
        const freshUser = await pb.collection('users').getOne(pb.authStore.model.id);
        logger.debug('User data refreshed successfully');
        setUser(freshUser as AuthUser);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      // Don't clear auth on temporary API failures - keep existing user session
      // Only clear if it's a specific authentication error (401/403)
      if (error?.status === 401 || error?.status === 403) {
        pb.authStore.clear();
        setUser(null);
      }
      // For other errors (network issues, 500s, etc.), keep the user logged in
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have any stored authentication
        if (pb.authStore.isValid && pb.authStore.model) {
          logger.debug('Found valid stored auth, setting user');
          setUser(pb.authStore.model as AuthUser);
          // Skip unnecessary API calls during initialization - PocketBase handles token refresh automatically
        } else {
          logger.debug('No valid stored auth found');
          setUser(null);
        }
      } catch (error) {
        logger.debug('Error during auth initialization:', error);
        // Don't clear auth store here, just set user to null
        setUser(null);
      }
      setIsLoading(false);
    };

    initAuth();

    // Listen for auth changes
    pb.authStore.onChange((token, model) => {
      logger.debug('Auth state changed', { hasToken: !!token, hasModel: !!model });
      if (model) {
        setUser(model as AuthUser);
      } else {
        setUser(null);
      }
    });

    // Set user offline when page unloads
    const handleBeforeUnload = () => {
      if (pb.authStore.model) {
        // Don't use navigator.sendBeacon as it causes authentication issues
        // The offline status will be handled by the components themselves
        logger.debug('Page unloading, offline status will be handled by components');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const startTime = performance.now();
    console.log('🔵 [LOGIN START]', new Date().toISOString());
    
    try {
      logger.debug('Attempting login', { email });
      
      // Step 1: Clear existing auth
      const clearStart = performance.now();
      if (pb.authStore.isValid) {
        console.log('🔄 [STEP 1] Clearing existing auth');
        logger.debug('Clearing existing auth session before new login');
        pb.authStore.clear();
        setUser(null);
      }
      console.log(`✅ [STEP 1 COMPLETE] Clear auth took ${(performance.now() - clearStart).toFixed(2)}ms`);
      
      // Step 2: Authenticate with PocketBase
      const authStart = performance.now();
      console.log('🔐 [STEP 2] Authenticating with PocketBase', { email });
      const authData = await pb.collection('users').authWithPassword(email, password);
      const authDuration = performance.now() - authStart;
      console.log(`✅ [STEP 2 COMPLETE] PocketBase auth took ${authDuration.toFixed(2)}ms`);
      
      const userId = authData.record?.id;
      console.log('👤 [USER ID]', userId);
      
      // Step 3: Verify user exists
      const verifyStart = performance.now();
      console.log('🔍 [STEP 3] Verifying user exists in database');
      try {
        const verifyUser = await pb.collection('users').getOne(userId);
        const verifyDuration = performance.now() - verifyStart;
        console.log(`✅ [STEP 3 COMPLETE] User verification took ${verifyDuration.toFixed(2)}ms`);
        
        // Verify email match
        if (verifyUser.email?.toLowerCase() !== email.toLowerCase()) {
          throw new Error('Email mismatch');
        }
        
        // Step 4: Set user state
        const setStateStart = performance.now();
        console.log('💾 [STEP 4] Setting user state');
        logger.info('Login successful and verified');
        setUser(authData.record as AuthUser);
        console.log(`✅ [STEP 4 COMPLETE] Set state took ${(performance.now() - setStateStart).toFixed(2)}ms`);
        
        // Step 5: Set user online
        console.log('🟢 [STEP 5] Setting user online');
        const userRecord = authData.record as AuthUser;
        if (userRecord?.id && userRecord?.role) {
          await setUserOnline(userRecord.id, userRecord.role);
        }
        console.log('✅ [STEP 5 COMPLETE] User set online');
        
        const totalDuration = performance.now() - startTime;
        console.log(`🎉 [LOGIN COMPLETE] Total time: ${totalDuration.toFixed(2)}ms (${(totalDuration/1000).toFixed(2)}s)`);
        console.log('📊 [BREAKDOWN]', {
          clear: `${(performance.now() - clearStart).toFixed(2)}ms`,
          auth: `${authDuration.toFixed(2)}ms`,
          verify: `${verifyDuration.toFixed(2)}ms`,
          setState: `${(performance.now() - setStateStart).toFixed(2)}ms`,
          total: `${totalDuration.toFixed(2)}ms`
        });
        
      } catch (verifyError) {
        console.error('❌ [VERIFICATION FAILED]', verifyError);
        logger.error('User verification failed after login', { userId, error: verifyError });
        pb.authStore.clear();
        setUser(null);
        
        throw {
          status: 500,
          message: 'Authentication error: User verification failed. Please contact support.',
          data: { verificationFailed: true }
        };
      }
    } catch (error: any) {
      const errorDuration = performance.now() - startTime;
      console.error('❌ [LOGIN FAILED]', {
        duration: `${errorDuration.toFixed(2)}ms`,
        status: error?.status,
        message: error?.message
      });
      
      logger.error('Login failed', {
        status: error?.status,
        message: error?.message
      });
      
      // Re-throw the error with preserved details for the UI to handle
      throw {
        status: error?.status,
        message: error?.message || error?.toString(),
        data: error?.data,
        originalError: error
      };
    }
  };

  const register = async (email: string, password: string, name: string, role: 'student' | 'tutor', phoneNumber?: string) => {
    try {
      // Set role and status based on user selection
      const finalRole = role;
      // Always start tutors as 'pending' - they will be auto-approved if settings allow
      let status = role === 'tutor' ? 'pending' : 'active';
      
      const userData = {
        email,
        password,
        passwordConfirm: password,
        name,
        role: finalRole,
        status,
        verified: false,
        phoneNumber: phoneNumber || '',
      };

      logger.debug('Registering user', { email, role });
      const user = await pb.collection('users').create(userData);
      logger.info('User created successfully', { userId: user.id, role });

      // Send notification to admin about new tutor registration
      if (role === 'tutor') {
        try {
          const { hybridNotificationService } = await import('@/services/hybridNotificationService');
          await hybridNotificationService.sendNotification({
            userId: 'admin_notification',
            type: 'general' as any,
            title: 'New Tutor Registration',
            message: `${name} has registered as a tutor and needs approval. Email: ${email}`
          });
          console.log('Admin notification sent for new tutor registration');
        } catch (notificationError) {
          console.error('Failed to send admin notification:', notificationError);
        }

        // Check if auto-approval is enabled and approve immediately if so
        // IMPORTANT: Auto-approval only updates the DATABASE, not the auth session
        // The session will be properly set up through the login call below
        try {
          console.log('🔍 Checking auto-approval settings for new tutor:', user.id);
          const { autoApproveIfEnabled } = await import('@/utils/checkAutoApprovalSettings');
          const wasAutoApproved = await autoApproveIfEnabled(user.id);
          
          if (wasAutoApproved) {
            console.log('✅ Tutor was auto-approved during registration');
            console.log('📊 Database updated, now will set up proper auth session through login');
            status = 'active';
            
            // Add a delay to ensure database consistency
            // This allows the auto-approval changes to propagate
            await new Promise(resolve => setTimeout(resolve, 1500));
            console.log('✅ Database consistency delay complete');
          } else {
            console.log('❌ Auto-approval was not successful or is disabled');
          }
        } catch (autoApprovalError) {
          console.error('Error checking auto-approval after registration:', autoApprovalError);
        }
      }

      // Auto-login after registration
      // CRITICAL: This is where the user's proper auth session is established
      // The login function will fetch fresh user data with the correct status
      logger.debug('Attempting auto-login after registration');
      console.log('🔐 Starting auto-login to establish proper auth session');
      await login(email, password);
      logger.info('Auto-login successful');
      console.log('✅ Auth session established successfully');
      
      // Force refresh user data to ensure latest status is reflected
      // This is especially important for tutors who may have been auto-approved
      if (role === 'tutor') {
        console.log('🔄 [POST-LOGIN] Refreshing user data after tutor registration');
        
        // Wait a bit for the database to fully commit the changes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force fetch fresh data directly from database, not from cache
        try {
          const freshUserData = await pb.collection('users').getOne(pb.authStore.model?.id, {
            requestKey: `refresh_after_register_${Date.now()}`
          });
          console.log('📊 [POST-LOGIN] Fresh user data from database:', {
            id: freshUserData.id,
            email: freshUserData.email,
            role: freshUserData.role,
            status: freshUserData.status
          });
          
          // Update the authStore with fresh data
          pb.authStore.save(pb.authStore.token, freshUserData);
          setUser(freshUserData as AuthUser);
          
          console.log('✅ [POST-LOGIN] User state forcefully updated with database data');
        } catch (error) {
          console.error('❌ [POST-LOGIN] Error fetching fresh user data:', error);
          // Fall back to regular refresh
          await refreshUser();
        }
        
        console.log('✅ [POST-LOGIN] User data refresh complete');
      }
      
      // The redirect will be handled by the component using useAuth
    } catch (error) {
      logger.error('Registration failed', { error });
      throw error;
    }
  };

  const logout = async () => {
    logger.debug('Starting logout process');
    
    if (isLoggingOut) {
      logger.debug('Logout already in progress, ignoring');
      return;
    }
    
    setIsLoggingOut(true);
    
    const currentUser = pb.authStore.model;
    
    try {
      // Set user offline immediately
      if (currentUser) {
        console.log('🔴 Setting user offline during logout:', { 
          userId: currentUser.id, 
          role: currentUser.role,
          user: currentUser 
        });
        await setUserOffline(currentUser.id, currentUser.role);
        console.log('✅ User set offline successfully');
      } else {
        console.log('❌ No current user found during logout');
      }
    } catch (error) {
      console.error('💥 Error setting user offline during logout:', error);
      logger.error('Error setting user offline during logout', { error });
    }
    
    // CRITICAL: Clear auth state and localStorage completely
    pb.authStore.clear();
    setUser(null);
    setIsLoading(false);
    
    // Additional security measure: Clear any potential cached auth data
    try {
      localStorage.removeItem('pocketbase_auth');
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
    
    // Force navigation to auth page
    logger.debug('Forcing navigation to auth page');
    window.location.href = '/auth';
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading, isLoggingOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
